import { faExclamationCircle, faInfoCircle, faExclamationTriangle, faCheckCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode, useEffect, useState } from "react";

export type AlertStyle = "filled" | "outline" | "none";

export type AlertVariant = "error" | "information" | "warning" | "success";

type AlertProps = {
  variant?: AlertVariant;
  style?: AlertStyle;
  children?: ReactNode;
  duration?: number;
  persistent?: boolean; // Renamed from open
};

export const Alert = ({
  variant = "error",
  style,
  duration = 10000,
  children,
  persistent: persistent,
}: AlertProps) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (persistent) {
      setIsVisible(true);
      return;
    }
    const timer = setTimeout(() => { setIsVisible(false); }, duration);
    return () => clearTimeout(timer);
  }, [duration, persistent]);

  if (!isVisible && !persistent) {
    return null;
  }

  let alertClass = "";
  let iconClass = "";
  let textClass = "ml-2";
  let icon = faExclamationCircle;

  switch (variant) {
    case "information":
      icon = faInfoCircle;
      if (style === "filled") {
        alertClass = "bg-blue-600 border border-blue-600 text-white px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-white mr-2 text-2xl";
      } else if (style === "outline") {
        alertClass = "border-2 border-blue-600 bg-white text-blue-900 px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-blue-600 mr-2 text-2xl";
      } else {
        alertClass = "bg-blue-50 border border-blue-400 text-blue-900 px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-blue-600 mr-2 text-2xl";
      }
      break;
    case "warning":
      icon = faExclamationTriangle;
      if (style === "filled") {
        alertClass = "bg-amber-600 border border-amber-600 text-white px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-white mr-2 text-2xl";
      } else if (style === "outline") {
        alertClass = "border-2 border-amber-600 bg-white text-amber-900 px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-amber-600 mr-2 text-2xl";
      } else {
        alertClass = "bg-amber-50 border border-amber-400 text-amber-900 px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-amber-600 mr-2 text-2xl";
      }
      break;
    case "success":
      icon = faCheckCircle;
      if (style === "filled") {
        alertClass = "bg-green-700 border border-green-700 text-white px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-white mr-2 text-2xl";
      } else if (style === "outline") {
        alertClass = "border-2 border-green-700 bg-white text-green-900 px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-green-700 mr-2 text-2xl";
      } else {
        alertClass = "bg-green-50 border border-green-500 text-green-900 px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-green-700 mr-2 text-2xl";
      }
      break;
    default: // error
      icon = faExclamationCircle;
      if (style === "filled") {
        alertClass = "bg-red-600 border border-red-600 text-white px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-white mr-2 text-2xl";
      } else if (style === "outline") {
        alertClass = "border-2 border-red-600 bg-white text-red-900 px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-red-600 mr-2 text-2xl";
      } else {
        alertClass = "bg-red-50 border border-red-400 text-red-900 px-4 py-3 rounded mb-4 flex items-center relative";
        iconClass = "text-red-600 mr-2 text-2xl";
      }
      break;
  }

  return (
    <div className={alertClass}>
      <FontAwesomeIcon icon={icon} className={iconClass} />
      <span className="font-bold"></span>
      <span className={textClass}>
        {children}
      </span>
      {!persistent && (
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 focus:outline-none"
          onClick={() => setIsVisible(false)}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} className="text-lg" />
        </button>
      )}
    </div>
  );
};
